import React from "react";
import { Link } from "react-router-dom";
import { withLocale } from "../utils/locale";

const BUSINESS_INFO = [
  { label: { ko: "상호명", vi: "Tên doanh nghiệp" }, value: "씨에스소프트" },
  { label: { ko: "대표자", vi: "Đại diện" }, value: "정원우" },
  { label: { ko: "사업자등록번호", vi: "Mã số doanh nghiệp" }, value: "425-43-01340" },
  {
    label: { ko: "사업장 주소", vi: "Địa chỉ" },
    value: "충청북도 진천군 덕산읍 대월로 127",
    wide: true,
  },
  {
    label: { ko: "상점 전화번호", vi: "Điện thoại" },
    value: "010-9174-6988",
    href: "tel:01091746988",
  },
];

const COPY = {
  ko: {
    tagline: "디스코드 서버를 발견하고, 알리고, 함께 성장하는 공간",
    business: "사업자 정보",
    serverExplore: "서버 찾기",
    profile: "서버추가",
    design: "프로필디자인",
    privacy: "개인정보 처리방침",
    terms: "이용약관",
    sellerTerms: "판매자 이용약관",
    refundPolicy: "환불 정책",
    email: "이메일 문의",
    phone: "전화 문의",
    copyright: "Disparty. All rights reserved.",
    disclaimer: "Disparty는 Discord Inc.와 제휴 관계가 없는 독립 서비스입니다.",
    intermediary:
      "크리에이터 상품의 통신판매중개자는 씨에스소프트이며, 거래 당사자가 아닙니다. 상품·거래 정보 및 거래에 대한 책임은 해당 판매자에게 있습니다. 다만 결제, 환불 접수와 분쟁 처리 창구는 Disparty가 제공합니다.",
  },
  vi: {
    tagline: "Nơi khám phá, giới thiệu và phát triển cộng đồng Discord",
    business: "Thông tin doanh nghiệp",
    serverExplore: "Tìm server",
    profile: "Hồ sơ",
    design: "Thiết kế",
    privacy: "Chính sách bảo mật",
    terms: "Điều khoản sử dụng",
    sellerTerms: "Điều khoản người bán",
    refundPolicy: "Chính sách hoàn tiền",
    email: "Liên hệ email",
    phone: "Liên hệ điện thoại",
    copyright: "Disparty. All rights reserved.",
    disclaimer: "Disparty là dịch vụ độc lập và không liên kết với Discord Inc.",
    intermediary: "Các sản phẩm của nhà sáng tạo được giao dịch giữa người mua và người bán. Disparty hỗ trợ thanh toán, hoàn tiền và giải quyết tranh chấp.",
  },
};

function FooterLink({ children, ...props }) {
  return (
    <Link
      className="w-fit text-sm leading-6 text-gray-400 transition-colors hover:text-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-4 focus-visible:ring-offset-[#111]"
      {...props}
    >
      {children}
    </Link>
  );
}

function FooterAnchor({ children, ...props }) {
  return (
    <a
      className="w-fit text-xs leading-5 text-gray-600 transition-colors hover:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-4 focus-visible:ring-offset-[#111]"
      {...props}
    >
      {children}
    </a>
  );
}

export default function BusinessFooter({ locale = "ko", className = "" }) {
  const language = locale === "vi" ? "vi" : "ko";
  const copy = COPY[language];
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className={`relative w-full overflow-hidden border-t border-gray-800 bg-[#111] font-maple text-left ${className}`}
    >
      <div className="relative mx-auto max-w-7xl px-5 py-11 sm:px-8 sm:py-14 lg:px-10 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-16 xl:grid-cols-[280px_minmax(0,1fr)] xl:gap-24">
          <div>
            <Link
              to={withLocale("/", language)}
              className="inline-block w-fit text-[1.8rem] font-normal leading-none text-white transition-colors hover:text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-4 focus-visible:ring-offset-[#111]"
            >
              Disparty!
            </Link>
            <p className="mt-5 max-w-xs text-sm leading-6 text-gray-500">{copy.tagline}</p>
          </div>

          <div>
            <section aria-labelledby="footer-business-title">
              <h2 id="footer-business-title" className="text-sm font-normal text-gray-600">
              {copy.business}
              </h2>
              <dl className="mt-5 grid gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
                {BUSINESS_INFO.map((item) => (
                  <div
                    key={item.label.ko}
                    className={`min-w-0 border-t border-gray-800 pt-3 ${item.wide ? "xl:col-span-2" : ""}`}
                  >
                    <dt className="text-xs leading-5 text-gray-600">{item.label[language]}</dt>
                    <dd className="mt-1 min-w-0 break-words text-sm leading-6 text-gray-500">
                      {item.href ? (
                        <a className="transition-colors hover:text-gray-300" href={item.href}>
                          {item.value}
                        </a>
                      ) : (
                        item.value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>

            <div className="mt-8 flex flex-col gap-5 border-t border-gray-800 pt-6 xl:flex-row xl:items-center xl:justify-between">
              <nav
                aria-label={language === "vi" ? "Liên kết chân trang" : "주요 바로가기"}
                className="flex flex-wrap items-center gap-x-8 gap-y-2"
              >
                <FooterLink to={withLocale("/", language)}>{copy.serverExplore}</FooterLink>
                <FooterLink to={withLocale("/profile", language)}>{copy.profile}</FooterLink>
                <FooterLink to={withLocale("/design", language)}>{copy.design}</FooterLink>
              </nav>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <FooterLink to={withLocale("/privacy", language)}>{copy.privacy}</FooterLink>
                <FooterLink to="/ko/terms">{copy.terms}</FooterLink>
                <FooterLink to="/ko/seller-terms">{copy.sellerTerms}</FooterLink>
                <FooterLink to="/ko/refund-policy">{copy.refundPolicy}</FooterLink>
                <FooterAnchor href="mailto:inhyuk0078@gmail.com">{copy.email}</FooterAnchor>
                <FooterAnchor href="tel:01091746988">{copy.phone}</FooterAnchor>
              </div>
            </div>
            <p className="mt-6 border-t border-gray-800 pt-5 text-xs leading-6 text-gray-600">
              {copy.intermediary}
            </p>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-gray-800 pt-6 text-xs leading-5 text-gray-600 sm:flex-row sm:items-center sm:justify-between">
          <p>© {currentYear} {copy.copyright}</p>
          <p className="sm:text-right">{copy.disclaimer}</p>
        </div>
      </div>
    </footer>
  );
}
